"use client";

import clsx from "clsx";
import {useEffect, useRef, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import ArtifactPanel from "@/components/chat/ArtifactPanel";
import GailConversationPanel, {type GailConversationPanelProps} from "@/components/chat/GailConversationPanel";
import {ArtifactPanelProvider, useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import {useResizablePanel} from "@/hooks/useResizablePanel";

const MIN_CHAT_WIDTH = 360;

type WorkspaceProps = Omit<GailConversationPanelProps, "overlayArtifacts"> & {
   children: ReactNode;
   showConversation: boolean;
   enabled?: boolean;
   conversationLabel?: string;
   /** Hide rather than unmount a closed conversation, so whatever it renders into the page survives. */
   keepConversationMounted?: boolean;
   /** Below md the conversation covers the page, so leave it closed there until the person opens it. */
   hideConversationBelowMd?: boolean;
};

function ConversationWorkspace({children, showConversation, enabled = true, conversationLabel, keepConversationMounted = false,
   hideConversationBelowMd = false, ...conversation}: WorkspaceProps) {
   const {t} = useTranslation("ask");
   const {conversationId} = conversation;
   const {activeArtifact, closeArtifact} = useArtifactPanel();
   const previousConversation = useRef(conversationId);
   useEffect(() => {
      if (previousConversation.current !== conversationId || !showConversation) closeArtifact();
      previousConversation.current = conversationId;
   }, [conversationId, showConversation, closeArtifact]);

   const {width, maxWidth, isDragging, handleMouseDown, handleKeyDown} = useResizablePanel({
      minWidth: MIN_CHAT_WIDTH,
      defaultWidth: 440,
      maxViewportFraction: 0.45,
      minCounterpartWidth: 620,
   });

   return (
      <div className={enabled ? "relative flex h-[calc(100dvh-4rem)] w-full min-w-0 overflow-hidden md:h-dvh" : "contents"}>
         <div className={enabled ? "relative h-full min-w-0 flex-1 overflow-hidden" : "contents"}>
            {/* Keep the editor mounted while previewing an artifact or changing chats. */}
            <div className={enabled ? "h-full" : "contents"} inert={!!activeArtifact} aria-hidden={!!activeArtifact}>
               {children}
            </div>
            {activeArtifact && (
               <div className="absolute inset-0 z-20 bg-panel">
                  <ArtifactPanel />
               </div>
            )}
         </div>
         {(showConversation || (enabled && keepConversationMounted)) && (
            <>
               {showConversation && <button
                  type="button"
                  role="slider"
                  aria-orientation="vertical"
                  aria-label={t("dock.resize")}
                  aria-valuemin={MIN_CHAT_WIDTH}
                  aria-valuemax={Math.round(Math.max(MIN_CHAT_WIDTH, maxWidth))}
                  aria-valuenow={Math.round(width)}
                  onMouseDown={handleMouseDown}
                  onKeyDown={handleKeyDown}
                  className={clsx(
                     "relative z-10 hidden w-1 shrink-0 cursor-col-resize border-0 bg-transparent p-0 transition-colors md:block",
                     "hover:bg-primary/30 focus:outline-none focus-visible:bg-primary/50 focus-visible:ring-2 focus-visible:ring-primary/40",
                     isDragging && "bg-primary/50"
                  )}
               >
                  <span className="absolute inset-y-0 -left-1 -right-1" aria-hidden="true" />
               </button>}
               <aside
                  aria-label={conversationLabel ?? t("workflowPanel.conversation")}
                  style={{width}}
                  className={clsx(
                     "h-full shrink-0 overflow-hidden border-l border-300 bg-panel max-md:absolute max-md:inset-0 max-md:z-10 max-md:!w-full",
                     !showConversation && "hidden",
                     hideConversationBelowMd && "max-md:hidden"
                  )}
               >
                  <GailConversationPanel {...conversation} overlayArtifacts={false} />
               </aside>
            </>
         )}
      </div>
   );
}

/** Shared editor and conversation layout, preserving editor state while the panel changes. */
export default function GailConversationWorkspace(props: WorkspaceProps) {
   return (
      <ArtifactPanelProvider>
         <ConversationWorkspace {...props} />
      </ArtifactPanelProvider>
   );
}
